
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CalendarIcon } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { saveMedicalVaccination, getVaccinationById } from '@/services/medicalService';
import { MedicalRecordLimitBanner } from '@/components/medical/MedicalRecordLimitBanner';
import { useMedicalRecordLimits } from '@/hooks/useMedicalRecordLimits';
import { useTranslation } from 'react-i18next';

export default function AddEditVaccinationForm() {
  const { t } = useTranslation();
  const { petId, vaccinationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEditing = vaccinationId !== 'new';
  const { hasReachedLimit } = useMedicalRecordLimits(petId);

  const [isAdministered, setIsAdministered] = useState(false);
  const [formData, setFormData] = useState({
    vaccine_name: '',
    date_due: new Date(),
    date_given: null as Date | null,
    vet_name: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && vaccinationId) {
      setLoading(true);
      getVaccinationById(vaccinationId)
        .then(data => {
          if (data) {
            setFormData({
              vaccine_name: data.vaccine_name,
              date_due: new Date(data.date_due),
              date_given: data.date_given ? new Date(data.date_given) : null,
              vet_name: data.vet_name || '',
              notes: data.notes || ''
            });
            setIsAdministered(!!data.date_given);
          }
        })
        .catch(err => {
          setError(t('medical.loadVaccinationError'));
          toast({
            title: t('common.error'),
            description: t('medical.loadVaccinationError'),
            variant: "destructive"
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [vaccinationId, isEditing, toast]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDateDueChange = (date: Date | undefined) => {
    if (date) {
      setFormData(prev => ({ ...prev, date_due: date }));
    }
  };

  const handleDateGivenChange = (date: Date | undefined) => {
    if (date) {
      setFormData(prev => ({ ...prev, date_given: date }));
    } else {
      setFormData(prev => ({ ...prev, date_given: null }));
    }
  };

  const handleIsAdministeredChange = (checked: boolean) => {
    setIsAdministered(checked);
    if (!checked) {
      setFormData(prev => ({ ...prev, date_given: null }));
    } else if (!formData.date_given) {
      // Default to today if toggling on and no date set
      setFormData(prev => ({ ...prev, date_given: new Date() }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!formData.vaccine_name.trim()) {
      setError(t('medical.vaccineNameRequired'));
      setLoading(false);
      return;
    }

    // Check if user has reached limit (only for new records)
    if (!isEditing && hasReachedLimit) {
      setError(t('medical.limitReached'));
      toast({
        title: t('medical.limitReachedTitle'),
        description: t('medical.limitReached'),
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    try {
      const vaccinationData = {
        ...formData,
        // Only include date_given if isAdministered is true
        date_given: isAdministered ? formData.date_given : null,
      };

      await saveMedicalVaccination(
        petId as string,
        vaccinationData,
        isEditing ? vaccinationId : undefined
      );

      toast({
        title: isEditing ? t('medical.vaccinationUpdated') : t('medical.vaccinationAdded'),
        description: t('medical.vaccinationSaved'),
      });

      navigate(`/medical/${petId}/vaccinations`);
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to save vaccination";
      setError(errorMessage);
      toast({
        title: t('common.error'),
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && isEditing) {
    return <div className="p-4 text-center">{t('medical.loadingVaccination')}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <form onSubmit={handleSubmit} className="space-y-6">
        {!isEditing && petId && (
          <MedicalRecordLimitBanner petId={petId} type={hasReachedLimit ? 'limit' : 'warning'} />
        )}

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="vaccine_name">{t('medical.form.vaccineName')}</Label>
          <Input
            id="vaccine_name"
            name="vaccine_name"
            value={formData.vaccine_name}
            onChange={handleInputChange}
            placeholder="e.g., Rabies, Distemper"
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="date_due">{t('medical.form.dueDate')}</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !formData.date_due && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date_due ? format(formData.date_due, "PPP") : t('medical.form.selectDate')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.date_due}
                onSelect={handleDateDueChange}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex items-center space-x-2 py-2">
          <Switch
            id="is_administered"
            checked={isAdministered}
            onCheckedChange={handleIsAdministeredChange}
          />
          <Label htmlFor="is_administered" className="cursor-pointer">
            {t('medical.form.vaccineAdministered')}
          </Label>
        </div>

        {isAdministered && (
          <div className="space-y-2">
            <Label htmlFor="date_given">{t('medical.form.dateAdministered')}</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.date_given && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date_given ? format(formData.date_given, "PPP") : t('medical.form.selectDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.date_given || undefined}
                  onSelect={handleDateGivenChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="vet_name">{t('medical.form.veterinarianName')}</Label>
          <Input
            id="vet_name"
            name="vet_name"
            value={formData.vet_name}
            onChange={handleInputChange}
            placeholder="Dr. Smith"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="notes">{t('medical.form.notes')}</Label>
          <Textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            placeholder="Additional information about this vaccination"
            rows={3}
          />
        </div>

        <Button
          type="submit"
          className="w-full bg-pet-purple hover:bg-pet-purple/90"
          disabled={loading || (!isEditing && hasReachedLimit)}
        >
          {loading ? t('common.saving') : isEditing ? t('medical.updateVaccination') : t('medical.addVaccinationButton')}
        </Button>
      </form>
    </div>
  );
}
